import type {ResidentMembership} from "@/src/modules/core/types/resident-membership";
import type {HouseholdMembership} from "@/src/modules/households/types";
import {
  HOUSEHOLD_PROPERTY_RULE_DEFAULTS,
  OCCUPANCY_OWNER_SEES_TENANT_DATA_OPTIONS,
  parseHouseholdBooleanRuleValue,
} from "@/src/modules/properties/lib/household-property-rules";
import {
  formatPropertyRuleSelectValue,
  resolvePropertyRuleSelectOptions,
} from "@/src/modules/properties/lib/property-rule-display";
import type {Property} from "@/src/modules/properties/types";

export const RESIDENT_HOME_NO_TENANT_DATA_COPY = "No tenant data available";

const OWNER_SEES_NONE: (typeof OCCUPANCY_OWNER_SEES_TENANT_DATA_OPTIONS)[number] = "None";

/** Same label the Households configure combobox shows for `occupancy_owner_sees_tenant_data`. */
export function getOwnerSeesTenantDataLabel(property: Property | null | undefined): string {
  const rule = property?.rules?.find((item) => item.key === "occupancy_owner_sees_tenant_data");
  const options = resolvePropertyRuleSelectOptions(
    "occupancy_owner_sees_tenant_data",
    rule?.options,
    {name: rule?.name, feature: rule?.feature ?? "households"},
  ) ?? [...OCCUPANCY_OWNER_SEES_TENANT_DATA_OPTIONS];
  const value = rule?.value ?? HOUSEHOLD_PROPERTY_RULE_DEFAULTS.occupancy_owner_sees_tenant_data;
  return formatPropertyRuleSelectValue(value, options);
}

/** Same boolean the Households configure toggle uses for `occupancy_owner_may_act_on_rented_unit`. */
export function getOwnerMayActOnRentedUnit(property: Property | null | undefined): boolean {
  const rule = property?.rules?.find((item) => item.key === "occupancy_owner_may_act_on_rented_unit");
  return parseHouseholdBooleanRuleValue(
    rule?.value ?? HOUSEHOLD_PROPERTY_RULE_DEFAULTS.occupancy_owner_may_act_on_rented_unit,
    true,
  );
}

type HideTenantOperationalDataArgs = {
  membership: ResidentMembership | null;
  property: Property | null | undefined;
  householdPending: boolean;
  occupant: HouseholdMembership | null;
  owner: HouseholdMembership | null;
};

/**
 * Landlord on a rented unit + rule **None**: hide household operational data
 * (permits, safelistings, citations, amenities, restriction banners).
 */
export function shouldHideResidentHomeTenantOperationalData({
  membership,
  property,
  householdPending,
  occupant,
  owner,
}: HideTenantOperationalDataArgs): boolean {
  if (!membership?.is_owner || membership.is_occupant) return false;
  if (getOwnerSeesTenantDataLabel(property) !== OWNER_SEES_NONE) return false;
  if (householdPending) return true;
  if (!occupant) return false;
  if (owner && occupant.user_id === owner.user_id) return false;
  return true;
}

/**
 * Landlord + **Owner may act on a rented unit** Off: hide safelisting **New**.
 * Uses membership immediately (`is_owner && !is_occupant`) so the button does not flash.
 */
export function shouldHideOwnerSafelistingCreate({
  membership,
  property,
  householdPending,
  occupant,
  owner,
}: HideTenantOperationalDataArgs): boolean {
  if (!membership?.is_owner || membership.is_occupant) return false;
  if (getOwnerMayActOnRentedUnit(property)) return false;
  if (householdPending) return true;
  if (!occupant) return false;
  if (owner && occupant.user_id === owner.user_id) return false;
  return true;
}
